"use client";

import Fragments from "@/components/fragments";
import Layouts from "@/components/layouts";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { BsPerson } from "react-icons/bs";
import {
  AiOutlineHome,
  AiOutlineMail,
  AiOutlineWhatsApp,
} from "react-icons/ai";
import { TbDeviceImac, TbMapSearch } from "react-icons/tb";
import { FaMagnifyingGlass, FaRegCalendarDays } from "react-icons/fa6";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useState } from "react";

const PickDeliverySchema = z.object({
  name: z.string(),
  devices: z.string(),
  email: z.string().email(),
  whatsapp: z.string(),
  alamat: z.string(),
  serialNumber: z.string(),
  areas: z.string(),
  date: z.string(),
  problem: z.string(),
});

const PickUpDeliveryForm = () => {
  const [isSubmit, setIsSubmit] = useState(false);
  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<z.infer<typeof PickDeliverySchema>>({
    resolver: zodResolver(PickDeliverySchema),
  });
  const fields = [
    {
      name: "name",
      label: "Name",
      type: "text",
      icon: <BsPerson />,
      placeholder: "Masukkan Nama",
    },
    {
      name: "email",
      label: "Email",
      type: "email",
      icon: <AiOutlineMail />,
      placeholder: "Masukkan Email",
    },
    {
      name: "whatsapp",
      label: "Whatsapp",
      type: "number",
      icon: <AiOutlineWhatsApp />,
      placeholder: "Masukkan Whatsapp",
    },
    {
      name: "alamat",
      label: "Alamat",
      type: "text",
      icon: <AiOutlineHome />,
      placeholder: "Masukkan Alamat",
    },
    {
      name: "devices",
      label: "Jenis Perangkat",
      type: "select",
      icon: <TbDeviceImac />,
      options: [
        { value: "macbook", label: "MacBook" },
        { value: "macbookAir", label: "MacBook Air" },
        { value: "macbookPro", label: "MacBook Pro" },
        { value: "imac", label: "iMac" },
        { value: "macmini", label: "Mac-Mini" },
        { value: "macpro", label: "Mac-Pro" },
      ],
      placeholder: "Masukkan Devices",
    },
    {
      name: "serialNumber",
      label: "Serial Number",
      type: "text",
      icon: <FaMagnifyingGlass />,
      placeholder: "Masukkan Serial Number",
    },
    {
      name: "areas",
      label: "Area",
      type: "select",
      icon: <TbMapSearch />,
      placeholder: "Masukkan Area Tempat Tinggal",
      options: [
        {
          value: "jakartaTimur",
          label: "Jakarta Timur",
        },
        {
          value: "jakartaBarat",
          label: "Jakarta Barat",
        },
        {
          value: "jakartaUtara",
          label: "Jakarta Utara",
        },
        {
          value: "jakartaPusat",
          label: "Jakarta Pusat",
        },
        {
          value: "jakartaSelatan",
          label: "Jakarta Selatan",
        },
        {
          value: "depok",
          label: "Depok",
        },
        {
          value: "bekasi",
          label: "Bekasi",
        },
        {
          value: "tangerang",
          label: "Tangerang",
        },
        {
          value: "tangerangSelatan",
          label: "Tangerang Selatan",
        },
      ],
    },
    {
      name: "date",
      label: "Date",
      type: "date",
      icon: <FaRegCalendarDays />,
      placeholder: "Masukkan Alamat",
    },
    {
      name: "problem",
      label: "Problem",
      type: "textarea",
      icon: <FaMagnifyingGlass />,
      placeholder: "Masukkan Permasalahan Pada Device Kamu",
    },
  ];

  const onSubmit = async (data: z.infer<typeof PickDeliverySchema>) => {
    const newData = {
      name: data.name,
      devices: data.devices,
      email: data.email,
      whatsapp: data.whatsapp,
      alamat: data.alamat,
      serialNumber: data.serialNumber,
      areas: data.areas,
      customerServices: "Kiky",
      date: data.date,
      problem: data.problem,
    };
    try {
      await axios.post(
        "https://server.macspace.id/send_email_delivery",
        newData
      );
      setIsSubmit(true);
      toast.success("Data Telah Terkirim", {
        position: "top-center",
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: false,
        progress: undefined,
        theme: "dark",
      });
      reset();
    } catch (error) {
      setIsSubmit(false);
      toast.error("Gagal Mengirim. Harap Hubungi Customer Service", {
        position: "top-center",
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: false,
        progress: undefined,
        theme: "dark",
      });
    }
  };
  return (
    <>
      <Layouts.Form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid lg:grid-cols-12 lg:gap-6">
          {fields.map((field, index) => (
            <div
              key={index}
              className={`${
                field.name === "areas" || field.type === "date"
                  ? "lg:col-span-6"
                  : field.type !== "textarea"
                  ? "lg:col-span-4"
                  : "lg:col-span-12"
              } mb-5`}
            >
              <Fragments.Controller
                key={field.name}
                {...field}
                control={control}
                errors={errors}
                type={field.type}
              />
            </div>
          ))}
        </div>
        <div className="grid lg:grid-cols-12">
          <div className="col-span-12">
            <Button className="w-full" type="submit">
              {isSubmit ? "Submit Success" : "Submit"}
            </Button>
          </div>
        </div>
        <div className="grid lg:grid-cols-12 mt-5">
          <div className="lg:col-span-12">
            <h1 className="text-xl uppercase my-5 text-center font-bold">
              Syarat dan ketentuan Free Pickup & Delivery
            </h1>
            <ul className="text-sm list-disc text-start">
              <li className="mt-2">
                Free Pickup & Delivery hanya berlaku untuk wilayah JADETABEK
              </li>
              <li className="mt-2">
                Pastikan customer dan kurir mengecek bersama kondisi unit di
                saat pengambilan atau pengantaran ( unit sebelum & sesudah di
                perbaiki )
              </li>
              <li className="mt-2">
                Pastikan tidak ada akesoris menempel pada unit yang akan di
                perbaiki.
              </li>
              <li className="mt-2">
                Unit akan kami pickup & delivery dengan memakai bubble wrap anti
                static
              </li>
              <li className="mt-2">
                Kontak kurir akan kami informasikan setelah kami menerima form
                ini
              </li>
              <li className="mt-2">
                Kami akan kenakan biaya pengambilan dan pengantaran kurir jika
                ada pembatalan secara sepihak dari customer atau tidak setuju
                untuk melakukan perbaikan. ( batal servis )
              </li>
            </ul>
          </div>
        </div>
      </Layouts.Form>
      <ToastContainer
        position="top-center"
        autoClose={2000}
        limit={1}
        hideProgressBar
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss={false}
        draggable={false}
        pauseOnHover={false}
        theme="dark"
      />
    </>
  );
};

export default PickUpDeliveryForm;
